import React, { useState } from "react";
import {
    UilCalender,
    UilCheckCircle,
    UilPaintTool,
    UilPlay,
    UilPlusCircle,
    UilTrashAlt,
} from "@iconscout/react-unicons";
import { format } from "date-fns";
import {
    Block,
    Button,
    Columns,
    Content,
    Tag
} from "react-bulma-components";
import { IconText } from "../common/Icon";
import Page from "../common/Page";
import { useSWRConfig } from "swr";
import { useNavigate } from "react-router-dom";
import LoadableButton from "../common/LoadableButton";
import ProjectContacts from "./ProjectContacts";
import ProjectInspirations from "./ProjectInspirations";
import ProjectShoppingItems from "./ProjectShoppingItems";
import ProjectDocuments from "./ProjectDocuments";
import ProjectPhotos from "./ProjectPhotos";
import AddExistingContactForm from "./AddExistingContactForm";
import AddNewContactForm from "./AddNewContactForm";
import AddInspirationForm from "./AddInspirationForm";
import AddShoppingItemForm from "./AddShoppingItemForm";
import {
    MODAL_ADD_EXISTING_CONTACT,
    MODAL_ADD_INSPIRATION,
    MODAL_ADD_NEW_CONTACT,
    MODAL_ADD_SHOPPING_ITEM
} from "./ProjectConstants";
import { fetcher } from "../utils/fetch";

export default function Project({ project }) {
    const navigate = useNavigate();
    const { mutate } = useSWRConfig();
    const [openModal, setOpenModal] = useState();

    const onDeleteProject = async () => {
        await fetcher(`/api/projects/${ project.id }`, {
            method: 'DELETE'
        });

        await mutate('/api/projects');
        await navigate('/projects');
    };

    const onDelete = async (uri) => {
        await fetcher(uri, {
            method: 'DELETE'
        });

        await mutate(`/api/projects/${ project.id }`);
    }

    // TODO: Ask for a date rather than assume "now"
    // TODO: Loading icon
    const onClickMarkAs = async (type) => {
        await fetcher(`/api/projects/${ project.id }`, {
            method: 'PATCH',
            body: JSON.stringify({
                [`${ type }_at`]: new Date().toISOString()
            })
        });

        await mutate(`/api/projects/${ project.id }`);
    };

    let markAsAction;
    if (!project.started_at) {
        markAsAction = (
            <LoadableButton color="info" icon={ UilPlay } onClick={ () => onClickMarkAs('started') }>
                Mark as started
            </LoadableButton>
        )
    } else if (!project.completed_at) {
        markAsAction = (
            <LoadableButton color="success" icon={ UilCheckCircle } onClick={ () => onClickMarkAs('completed') }>
                Mark as complete
            </LoadableButton>
        )
    }

    const onDropFiles = async (type, files) => {
        for (const file of files) {
            const formData = new FormData();
            formData.append('file', file);

            try {
                await fetcher(`/api/projects/${ project.id }/${ type }/upload`, {
                    method: 'POST',
                    body: formData,
                    headers: {
                        'Content-Type': null
                    }
                });

                await mutate(`/api/projects/${ project.id }`);
            } catch (e) {
                // TODO
                console.error(e);
            }
        }
    }

    const projectSectionOptions = {
        onDropFiles: onDropFiles,
        onTriggerDelete: onDelete,
        onTriggerModal: setOpenModal,
        project: project.id
    }

    return (
        <Page icon={ UilPaintTool } title={ project.name }
              subtitle={ `Created ${ format(new Date(project.created_at), 'dd MMM yyyy') }` } actions={
            <Button.Group>
                { markAsAction }

                {/* TODO: Confirmation before delete */ }
                <LoadableButton color="danger" icon={ UilTrashAlt } onClick={ onDeleteProject }>
                    Delete project
                </LoadableButton>
            </Button.Group>
        }>
            <hr/>

            <Columns>
                <Columns.Column size="two-thirds">
                    <ProjectShoppingItems { ...projectSectionOptions } shoppingItems={ project.shopping_items }/>
                    <ProjectInspirations { ...projectSectionOptions } inspirations={ project.inspiration }/>
                    <ProjectContacts { ...projectSectionOptions } contacts={ project.contacts }/>
                    <ProjectDocuments { ...projectSectionOptions } documents={ project.documents }/>
                    <ProjectPhotos { ...projectSectionOptions } photos={ project.photos }/>
                </Columns.Column>

                <Columns.Column className="has-background-white-bis" p={ 4 }>
                    <Block>
                        <Content>
                            <p>{ project.description }</p>
                        </Content>
                    </Block>

                    <Block>
                        <div className="my-2">
                            <IconText icon={ UilPlusCircle }>
                                Created on <strong>
                                <time
                                    dateTime={ project.created_at }>{ format(new Date(project.created_at), 'd MMM yyyy') }</time>
                            </strong>
                            </IconText>
                        </div>

                        {
                            project.started_at == null
                                ? null
                                : (
                                    <div className="my-2">
                                        <IconText color="info" icon={ UilCalender }>
                                            Started on <strong>
                                            <time
                                                dateTime={ project.started_at }>{ format(new Date(project.started_at), 'd MMM yyyy') }</time>
                                        </strong>
                                        </IconText>
                                    </div>
                                )
                        }

                        {
                            project.completed_at == null
                                ? null
                                : (
                                    <div className="my-2">
                                        <IconText color="success" icon={ UilCheckCircle }>
                                            Completed on <strong>
                                            <time
                                                dateTime={ project.completed_at }>{ format(new Date(project.completed_at), 'd MMM yyyy') }</time>
                                        </strong>
                                        </IconText>
                                    </div>
                                )
                        }
                    </Block>

                    <hr className="my-4"/>

                    <Block>
                        <h3 className="has-text-weight-semibold pb-2">Rooms</h3>

                        <div>
                            {
                                project.rooms.map(room => <Tag key={ room.id } color="info"
                                                               colorVariant="light">{ room.name }</Tag>)
                            }
                        </div>
                    </Block>
                </Columns.Column>
            </Columns>

            {/* TODO: Constantise these strings */ }
            <AddExistingContactForm project={ project } onClose={ () => setOpenModal(null) }
                                    show={ openModal === MODAL_ADD_EXISTING_CONTACT }/>

            <AddNewContactForm project={ project } onClose={ () => setOpenModal(null) }
                               show={ openModal === MODAL_ADD_NEW_CONTACT }/>

            <AddInspirationForm project={ project } onClose={ () => setOpenModal(null) }
                                show={ openModal === MODAL_ADD_INSPIRATION }/>

            <AddShoppingItemForm project={ project } onClose={ () => setOpenModal(null) }
                                 show={ openModal === MODAL_ADD_SHOPPING_ITEM }/>
        </Page>
    )
}